import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Formik, Form, FastField, FastFieldProps } from 'formik';
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaPlus,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import { FormGroup, Modal, ModalContent, RotateLoader, Switch } from '@lib/components';
import { MobxComponent } from '../../../../../mobx/components';
import { UI } from '../../../../../core/ui';
import {
  DEFAULT_GIFT_CARD_PROGRAM,
  DEFAULT_LOYALTY_PROGRAM,
  isNativeLoyaltyConnected,
  majorStringToMinor,
  minorToMajorString,
  optionalFormInt,
} from './helpers';
import {
  LC,
  LoyaltyButton,
  LoyaltyCard,
  LoyaltyCardHeading,
  LoyaltyShell,
} from './shell';

// ─── Figma design tokens ─────────────────────────────────────────────────────
const FG = {
  navy: '#1a1a54',
  labelColor: '#414651',
  optionalColor: '#717680',
  sectionTitle: '#1e293b',
  sectionDesc: '#64748b',
  inputText: '#181d27',
  inputBorder: '#d5d7da',
  white: '#ffffff',
};

const figmaInputStyle: React.CSSProperties = {
  width: '100%',
  height: 32,
  padding: '0 12px',
  fontSize: 12,
  lineHeight: '18px',
  color: FG.inputText,
  background: FG.white,
  border: `1px solid ${FG.inputBorder}`,
  borderRadius: 8,
  boxShadow: '0px 1px 1px rgba(10,13,18,0.05)',
  outline: 'none',
  boxSizing: 'border-box',
};

const figmaTextareaStyle: React.CSSProperties = {
  ...figmaInputStyle,
  height: 120,
  resize: 'vertical',
  padding: '8px 12px',
  lineHeight: '18px',
};

/** A compact label-above-input field as shown in the Figma design. */
function FigmaField(props: {
  label: string;
  optional?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 0 140px', minWidth: 140, ...props.style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: FG.labelColor, lineHeight: '18px', whiteSpace: 'nowrap' }}>
          {props.label}
        </span>
        {props.optional && (
          <span style={{ fontSize: 10, color: FG.optionalColor, lineHeight: '18px', whiteSpace: 'nowrap' }}>
            {props.optional}
          </span>
        )}
      </div>
      {props.children}
    </div>
  );
}

/** A section group: small bold title + muted description above content. */
function SectionGroup(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: FG.sectionTitle }}>{props.title}</p>
        {props.description && (
          <p style={{ margin: 0, fontSize: 11, color: FG.sectionDesc, lineHeight: '18px' }}>{props.description}</p>
        )}
      </div>
      {props.children}
    </div>
  );
}

/** Inline field grid — children are FigmaField elements that wrap with gap. */
function FieldRow(props: { children: React.ReactNode; gap?: number }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: props.gap ?? 24, alignItems: 'flex-start' }}>
      {props.children}
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {}

type SettingsTab = 'points' | 'gift_cards' | 'display' | 'platform';

interface State {
  loading: boolean;
  saving: boolean;
  error: string;
  loyalty: typeof DEFAULT_LOYALTY_PROGRAM;
  giftCard: typeof DEFAULT_GIFT_CARD_PROGRAM;
  loyaltyScope: 'organisation' | 'restaurant';
  display: {
    name: string;
    description: string;
    primaryColour: string;
    secondaryColour: string;
    goldPoints: string;
    goldDiscount: string;
    birthdayDiscount: string;
    giftDenominations: string;
    giftMinAmount: string;
    giftMaxAmount: string;
    giftPurchaseDiscount: string;
  };
  linkedRestaurantIds: string[];
  addRestaurantActive: boolean;
  restaurantQuery: string;
  restaurantSelection: string[];
  settingsTab: SettingsTab;
}

type FormValues = {
  loyalty_enabled: boolean;
  base_rate: number;
  bonus_multiplier: number;
  min_spend: number;
  redeem_points: number;
  redeem_value: string;
  max_redemption_percent: number;
  min_redemption_points: number;
  points_expiry_days: string | number;
  gift_card_enabled: boolean;
  gift_card_expiry_enabled: boolean;
  gift_card_expiry_days: string | number;
  gift_card_purchase_enabled: boolean;
  gift_card_allow_custom: boolean;
  cash_rate: string | number;
  card_rate: string | number;
};

// ─── Component ────────────────────────────────────────────────────────────────

@inject('store')
@observer
export class RestaurantLoyaltySettings extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      error: '',
      loyalty: DEFAULT_LOYALTY_PROGRAM,
      giftCard: DEFAULT_GIFT_CARD_PROGRAM,
      loyaltyScope: 'organisation',
      display: {
        name: '',
        description: '',
        primaryColour: '#FB2F2F',
        secondaryColour: '#FFBBBB',
        goldPoints: '',
        goldDiscount: '',
        birthdayDiscount: '',
        giftDenominations: '50, 100, 150, 200',
        giftMinAmount: '',
        giftMaxAmount: '',
        giftPurchaseDiscount: '0',
      },
      linkedRestaurantIds: [],
      addRestaurantActive: false,
      restaurantQuery: '',
      restaurantSelection: [],
      settingsTab: 'points',
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    if (!isNativeLoyaltyConnected(restaurant)) {
      this.setState({ loading: false, error: 'Connect Quaddo in Integrations first.' });
      return;
    }

    this.setState({ loading: true, error: '' });
    try {
      const [loyaltyRes, giftRes] = await Promise.all([
        store.api.native_loyalty_loyalty_program({ restaurant_id: restaurant._id }),
        store.api.native_loyalty_gift_card_program({ restaurant_id: restaurant._id }),
      ]);
      if (loyaltyRes.outcome) {
        this.setState({ loading: false, error: loyaltyRes.message || 'Failed to load loyalty programme' });
        return;
      }
      if (giftRes.outcome) {
        this.setState({ loading: false, error: giftRes.message || 'Failed to load gift card programme' });
        return;
      }
      const loyaltyProgram = loyaltyRes.data?.program;
      const giftProgram = giftRes.data?.program;
      this.setState({
        loading: false,
        loyaltyScope: loyaltyRes.data?.merchant?.loyaltyScope ?? 'organisation',
        loyalty: {
          ...DEFAULT_LOYALTY_PROGRAM,
          ...(loyaltyProgram || {}),
          earn: { ...DEFAULT_LOYALTY_PROGRAM.earn, ...(loyaltyProgram?.earn || {}) },
          redemption: { ...DEFAULT_LOYALTY_PROGRAM.redemption, ...(loyaltyProgram?.redemption || {}) },
        },
        giftCard: { ...DEFAULT_GIFT_CARD_PROGRAM, ...(giftProgram || {}) },
        display: {
          ...this.state.display,
          name: loyaltyProgram?.displayName ?? '',
          description: loyaltyProgram?.description ?? '',
          primaryColour: loyaltyProgram?.primaryColour ?? this.state.display.primaryColour,
          secondaryColour: loyaltyProgram?.secondaryColour ?? this.state.display.secondaryColour,
          goldPoints: loyaltyProgram?.goldTier ? String(loyaltyProgram.goldTier.minPoints) : '',
          goldDiscount: loyaltyProgram?.goldTier ? String(loyaltyProgram.goldTier.discountPercent) : '',
          birthdayDiscount: loyaltyProgram?.birthdayDiscount
            ? String(loyaltyProgram.birthdayDiscount.discountPercent)
            : '',
          ...this.giftPurchaseDisplay(giftProgram),
        },
      });
    } catch (e) {
      this.setState({ loading: false, error: 'Failed to load loyalty settings' });
    }
  };

  canManage() {
    if (this.injected.store.restrictions.restaurant.loyalty_programme_manage !== true) return false;
    return this.state.loyaltyScope === 'restaurant';
  }

  hasManagePermission() {
    return this.injected.store.restrictions.restaurant.loyalty_programme_manage === true;
  }

  initialValues(): FormValues {
    const { loyalty, giftCard } = this.state;
    return {
      loyalty_enabled: !!loyalty.enabled,
      base_rate: Number(loyalty.earn.baseRate),
      bonus_multiplier: Number(loyalty.earn.bonusMultiplier),
      min_spend: Number(loyalty.earn.minSpend),
      redeem_points: Number(loyalty.redemption.redeemPoints),
      redeem_value: minorToMajorString(loyalty.redemption.redeemValue, this.currencyPrecision()),
      max_redemption_percent: Number(loyalty.redemption.maxRedemptionPercent),
      min_redemption_points: Number(loyalty.redemption.minRedemptionPoints),
      points_expiry_days: loyalty.pointsExpiryDays == null ? '' : String(loyalty.pointsExpiryDays),
      gift_card_enabled: !!giftCard.enabled,
      gift_card_expiry_enabled: !!giftCard.expiryEnabled,
      gift_card_expiry_days: giftCard.defaultExpiryDays == null ? '' : String(giftCard.defaultExpiryDays),
      gift_card_purchase_enabled: giftCard.purchaseEnabled !== false,
      gift_card_allow_custom: !!giftCard.allowCustomAmount,
      cash_rate:
        loyalty.earn.paymentMethodRates.cash == null ? '' : String(loyalty.earn.paymentMethodRates.cash),
      card_rate:
        loyalty.earn.paymentMethodRates.card == null ? '' : String(loyalty.earn.paymentMethodRates.card),
    };
  }

  currencyPrecision(): number {
    const currency = this.injected.store.restaurant?.settings?.region?.currency;
    return typeof currency?.precision === 'number' ? currency.precision : 2;
  }

  earnRateCopy(): { label: string; suffix: string; description: string } {
    const code = this.injected.store.restaurant?.settings?.region?.currency?.code;
    const precision = this.currencyPrecision();
    const major = precision <= 0 ? '1' : `1.${'0'.repeat(precision)}`;
    const whole = code ? `${major} ${code}` : major;
    const unit = code ? `1 ${code}` : '1';
    return {
      label: `Base rate`,
      suffix: `points per ${unit}`,
      description: `Enter 1 to award 1 point for every ${whole}. Payment-method names are typed in by hand so they must match what the POS sends. If not listed, the base rate is used.`,
    };
  }

  save = async (values: FormValues) => {
    if (!this.canManage()) return;
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({ saving: true, error: '' });

    const redeemValue = majorStringToMinor(values.redeem_value, this.currencyPrecision());
    if (redeemValue == null) {
      this.setState({ saving: false, error: 'Redemption value is invalid for this currency' });
      return;
    }

    const pointsExpiryDays = optionalFormInt(values.points_expiry_days);
    const giftExpiryDays = optionalFormInt(values.gift_card_expiry_days);
    const giftPurchase = this.giftPurchasePayload();
    if ('error' in giftPurchase) {
      this.setState({ saving: false, error: giftPurchase.error });
      return;
    }

    const paymentMethodRates: { [key: string]: number } = { ...this.state.loyalty.earn.paymentMethodRates };

    const loyaltyProgram = {
      ...this.state.loyalty,
      enabled: values.loyalty_enabled,
      earn: {
        ...this.state.loyalty.earn,
        baseRate: Number(values.base_rate),
        paymentMethodRates,
        bonusMultiplier: Number(values.bonus_multiplier),
        minSpend: Number(values.min_spend),
        rounding: 'up' as const,
      },
      redemption: {
        ...this.state.loyalty.redemption,
        redeemPoints: Number(values.redeem_points),
        redeemValue,
        maxRedemptionPercent: Number(values.max_redemption_percent),
        minRedemptionPoints: Number(values.min_redemption_points),
      },
      pointsExpiryDays: pointsExpiryDays,
      displayName: this.state.display.name.trim() === '' ? null : this.state.display.name.trim(),
      description: this.state.display.description.trim() === '' ? null : this.state.display.description.trim(),
      primaryColour: this.state.display.primaryColour || null,
      secondaryColour: this.state.display.secondaryColour || null,
      goldTier:
        this.state.display.goldPoints.trim() === '' || this.state.display.goldDiscount.trim() === ''
          ? null
          : {
              minPoints: Number(this.state.display.goldPoints),
              discountPercent: Number(this.state.display.goldDiscount),
            },
      birthdayDiscount:
        this.state.display.birthdayDiscount.trim() === ''
          ? null
          : { discountPercent: Number(this.state.display.birthdayDiscount) },
    };

    const giftCardProgram = {
      ...this.state.giftCard,
      enabled: values.gift_card_enabled,
      expiryEnabled: values.gift_card_expiry_enabled,
      defaultExpiryDays: giftExpiryDays,
      purchaseEnabled: values.gift_card_purchase_enabled,
      allowCustomAmount: values.gift_card_allow_custom,
      ...giftPurchase,
    };

    try {
      const loyaltyRes = await store.api.native_loyalty_loyalty_program_update({
        restaurant_id: restaurant._id,
        program: loyaltyProgram,
      });
      if (loyaltyRes.outcome) {
        this.setState({ saving: false, error: loyaltyRes.message || 'Failed to save loyalty programme' });
        return;
      }

      const giftRes = await store.api.native_loyalty_gift_card_program_update({
        restaurant_id: restaurant._id,
        program: giftCardProgram,
      });
      if (giftRes.outcome) {
        this.setState({ saving: false, error: giftRes.message || 'Failed to save gift card programme' });
        return;
      }

      this.setState({
        saving: false,
        loyalty: { ...DEFAULT_LOYALTY_PROGRAM, ...(loyaltyRes.data || loyaltyProgram) },
        giftCard: { ...DEFAULT_GIFT_CARD_PROGRAM, ...(giftRes.data || giftCardProgram) },
      });
      UI.notification.success('Loyalty settings updated');
    } catch (e) {
      this.setState({ saving: false, error: 'Failed to save loyalty settings' });
    }
  };

  giftPurchaseDisplay(program: any) {
    const precision = this.currencyPrecision();
    const source = program || DEFAULT_GIFT_CARD_PROGRAM;
    const denominations = Array.isArray(source.denominations) ? source.denominations : DEFAULT_GIFT_CARD_PROGRAM.denominations;
    return {
      giftDenominations: denominations.map((minor: number) => minorToMajorString(minor, precision)).join(', '),
      giftMinAmount: source.minAmount == null ? '' : minorToMajorString(source.minAmount, precision),
      giftMaxAmount: source.maxAmount == null ? '' : minorToMajorString(source.maxAmount, precision),
      giftPurchaseDiscount:
        source.purchaseDiscountPercent == null ? '0' : String(source.purchaseDiscountPercent),
    };
  }

  giftPurchasePayload():
    | { denominations: number[]; minAmount: number | null; maxAmount: number | null; purchaseDiscountPercent: number }
    | { error: string } {
    const precision = this.currencyPrecision();
    const parts = this.state.display.giftDenominations.split(',').map(part => part.trim()).filter(Boolean);
    if (parts.length === 0) return { error: 'Enter at least one gift card value' };
    if (parts.length > 8) return { error: 'Gift card values can have at most 8 amounts' };
    const denominations: number[] = [];
    for (const part of parts) {
      const minor = majorStringToMinor(part, precision);
      if (minor == null || minor <= 0) return { error: `Gift card value "${part}" is invalid` };
      denominations.push(minor);
    }
    const minRaw = this.state.display.giftMinAmount.trim();
    const maxRaw = this.state.display.giftMaxAmount.trim();
    const minAmount = minRaw === '' ? null : majorStringToMinor(minRaw, precision);
    const maxAmount = maxRaw === '' ? null : majorStringToMinor(maxRaw, precision);
    if (minRaw !== '' && (minAmount == null || minAmount <= 0)) return { error: 'Minimum custom amount is invalid' };
    if (maxRaw !== '' && (maxAmount == null || maxAmount <= 0)) return { error: 'Maximum custom amount is invalid' };
    if (minAmount != null && maxAmount != null && minAmount > maxAmount) {
      return { error: 'Minimum custom amount must not be greater than the maximum' };
    }
    const discount = optionalFormInt(this.state.display.giftPurchaseDiscount);
    if (discount == null || discount < 0 || discount > 90) {
      return { error: 'Purchase discount must be a whole number from 0 to 90' };
    }
    return { denominations, minAmount, maxAmount, purchaseDiscountPercent: discount };
  }

  setDisplay(key: keyof State['display'], value: string) {
    this.setState({ display: { ...this.state.display, [key]: value } });
  }

  restaurants() {
    return this.injected.store.restaurants.items || [];
  }

  restaurantById(id: string) {
    return this.restaurants().find((r: any) => r._id === id);
  }

  /** Compact Formik number input for Figma-style grid. */
  numField(name: keyof FormValues, disabled?: boolean) {
    return (
      <FastField name={name}>
        {({ field }: FastFieldProps) => (
          <input type="number" {...field} disabled={disabled} style={figmaInputStyle} />
        )}
      </FastField>
    );
  }

  // ─── Tab: Loyalty Points ───────────────────────────────────────────────────

  renderPointsTab(form: any, canManage: boolean) {
    const { display } = this.state;
    const rate = this.earnRateCopy();
    const currencyCode = this.injected.store.restaurant?.settings?.region?.currency?.code ?? '';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {/* Points Settings */}
        <SectionGroup
          title="Points Settings"
          description={rate.description}
        >
          {/* Enable toggle row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <FastField name="loyalty_enabled">
              {({ field }: FastFieldProps) => (
                <Switch
                  id="loyalty-program-enabled"
                  checked={!!field.value}
                  onChange={e => canManage && form.setFieldValue('loyalty_enabled', e.target.checked)}
                />
              )}
            </FastField>
            <div>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: FG.labelColor }}>Enable Loyalty Points</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: FG.sectionDesc }}>Loyalty points will be enabled and accessible</p>
            </div>
          </div>

          {/* Fields grid */}
          <FieldRow>
            <FigmaField label={rate.label}>
              {this.numField('base_rate', !canManage)}
            </FigmaField>
            <FigmaField label="Multiplier">
              {this.numField('bonus_multiplier', !canManage)}
            </FigmaField>
            <FigmaField label="Minimum spend" optional="(Minor units)">
              {this.numField('min_spend', !canManage)}
            </FigmaField>
            <FigmaField label="Gold Tier" optional="(Minimum points this year)">
              <input
                type="number"
                value={display.goldPoints}
                disabled={!canManage}
                placeholder="Off"
                onChange={e => this.setDisplay('goldPoints', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
            <FigmaField label="Discount Percentage" optional="(Gold %)">
              <input
                type="number"
                value={display.goldDiscount}
                disabled={!canManage}
                placeholder="Off"
                onChange={e => this.setDisplay('goldDiscount', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
            <FigmaField label="Birthday Discount" optional="(Percentage)">
              <input
                type="number"
                value={display.birthdayDiscount}
                disabled={!canManage}
                placeholder="Off"
                onChange={e => this.setDisplay('birthdayDiscount', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
          </FieldRow>
        </SectionGroup>

        {/* Redemption Rate */}
        <SectionGroup
          title="Redemption Rate"
          description={`How many points a customer needs to spend to get a dollar off. For example, 100 / 1.00 means 100 points equals $1.00 ${currencyCode} off.`}
        >
          <FieldRow>
            <FigmaField label="Points">
              {this.numField('redeem_points', !canManage)}
            </FigmaField>
            <FigmaField label={`Value${currencyCode ? ` (${currencyCode})` : ''}`}>
              {this.numField('redeem_value', !canManage)}
            </FigmaField>
            <FigmaField label="Max Redemption" optional="(Percentage)">
              {this.numField('max_redemption_percent', !canManage)}
            </FigmaField>
            <FigmaField label="Min. Redemption Points">
              {this.numField('min_redemption_points', !canManage)}
            </FigmaField>
            <FigmaField label="Points Expiry" optional="(Days)">
              <FastField name="points_expiry_days">
                {({ field }: FastFieldProps) => (
                  <input
                    type="number"
                    {...field}
                    disabled={!canManage}
                    placeholder="Never"
                    style={figmaInputStyle}
                  />
                )}
              </FastField>
            </FigmaField>
          </FieldRow>
        </SectionGroup>
      </div>
    );
  }

  // ─── Tab: Gift Cards ───────────────────────────────────────────────────────

  renderGiftCardTab(form: any, canManage: boolean) {
    const { display } = this.state;
    const currencyCode = this.injected.store.restaurant?.settings?.region?.currency?.code ?? '';
    const cc = currencyCode ? ` (${currencyCode})` : '';

    /** Inline toggle row: switch on the left, label + description on the right. */
    const ToggleRow = (props: {
      id: string;
      name: keyof FormValues;
      label: string;
      description: string;
    }) => (
      <FastField name={props.name}>
        {({ field }: FastFieldProps) => (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Switch
              id={props.id}
              checked={!!field.value}
              onChange={e => canManage && form.setFieldValue(props.name, e.target.checked)}
            />
            <div>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: FG.labelColor }}>{props.label}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: FG.sectionDesc }}>{props.description}</p>
            </div>
          </div>
        )}
      </FastField>
    );

    const expiryEnabled = !!form.values.gift_card_expiry_enabled;
    const purchaseEnabled = !!form.values.gift_card_purchase_enabled;
    const allowCustom = !!form.values.gift_card_allow_custom;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Row 1: two toggles side by side */}
        <FieldRow gap={32}>
          <div style={{ flex: '1 0 220px' }}>
            <ToggleRow
              id="gift-card-program-enabled"
              name="gift_card_enabled"
              label="Enable Gift Card"
              description="Enabling this will allow Gift Card to be accessible"
            />
          </div>
          <div style={{ flex: '1 0 220px' }}>
            <ToggleRow
              id="gift-card-expiry-enabled"
              name="gift_card_expiry_enabled"
              label="Enabled Gift Card Expiration"
              description="Enabling this will have Gift Card expiration"
            />
          </div>
        </FieldRow>

        {/* Default Expiry Days — shown only when expiry is enabled */}
        {expiryEnabled && (
          <FigmaField label="Default Expiry" optional="(Days)" style={{ flex: '0 0 auto', minWidth: 160, maxWidth: 200 }}>
            {this.numField('gift_card_expiry_days', !canManage)}
          </FigmaField>
        )}

        {/* Divider */}
        <div style={{ borderTop: `1px solid ${FG.inputBorder}` }} />

        {/* Selling Gift Cards Online section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: FG.sectionTitle }}>Selling Gift Cards Online</p>
          <p style={{ margin: 0, fontSize: 11, color: FG.sectionDesc, lineHeight: '18px' }}>
            {`What customers can buy in the loyalty portal. Amounts in ${currencyCode || 'the restaurant currency'}. Purchases still need confirming before the balance is added.`}
          </p>
        </div>

        {/* Purchase enabled toggle */}
        <ToggleRow
          id="gift-card-purchase-enabled"
          name="gift_card_purchase_enabled"
          label="Enable Customers Can Buy Gift Cards Online"
          description="Enabling this customers can buy gift cards online but requires the programme to be enabled"
        />

        {/* Card Values — shown only when purchase is enabled */}
        {purchaseEnabled && (
          <FigmaField label={`Card Values${cc}`} style={{ maxWidth: 320, flex: '1 0 0' }}>
            <input
              type="text"
              value={display.giftDenominations}
              disabled={!canManage}
              placeholder="50, 100, 150, 200"
              onChange={e => this.setDisplay('giftDenominations', e.target.value)}
              style={figmaInputStyle}
            />
          </FigmaField>
        )}

        {/* Allow custom amount toggle — shown only when purchase is enabled */}
        {purchaseEnabled && (
          <ToggleRow
            id="gift-card-allow-custom"
            name="gift_card_allow_custom"
            label="Allow Entering Amount"
            description="Enabling this will let customers enter their own amount"
          />
        )}

        {/* Min/Max — shown only when allow custom is enabled */}
        {purchaseEnabled && allowCustom && (
          <FieldRow>
            <FigmaField label="Minimum Allowed" optional={cc || undefined}>
              <input
                type="text"
                value={display.giftMinAmount}
                disabled={!canManage}
                placeholder="0"
                onChange={e => this.setDisplay('giftMinAmount', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
            <FigmaField label="Maximum Allowed" optional={cc || undefined}>
              <input
                type="text"
                value={display.giftMaxAmount}
                disabled={!canManage}
                placeholder="0"
                onChange={e => this.setDisplay('giftMaxAmount', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
            <FigmaField label="Purchase Discount" optional="(Percentage)">
              <input
                type="number"
                value={display.giftPurchaseDiscount}
                disabled={!canManage}
                onChange={e => this.setDisplay('giftPurchaseDiscount', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
          </FieldRow>
        )}
      </div>
    );
  }

  // ─── Tab: Display ──────────────────────────────────────────────────────────

  renderDisplayTab(canManage: boolean) {
    const { display } = this.state;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <SectionGroup title="Loyalty Branding">
          <FieldRow>
            <FigmaField label="Loyalty name" style={{ flex: '2 0 200px' }}>
              <input
                type="text"
                value={display.name}
                placeholder="General Loyalty"
                disabled={!canManage}
                onChange={e => this.setDisplay('name', e.target.value)}
                style={figmaInputStyle}
              />
            </FigmaField>
            <FigmaField label="Primary colour" style={{ flex: '1 0 140px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.1)',
                    background: display.primaryColour,
                    flexShrink: 0,
                  }}
                />
                <input
                  type="text"
                  value={display.primaryColour}
                  disabled={!canManage}
                  onChange={e => this.setDisplay('primaryColour', e.target.value)}
                  style={{ ...figmaInputStyle, flex: '1 0 0' }}
                />
              </div>
            </FigmaField>
            <FigmaField label="Secondary colour" style={{ flex: '1 0 140px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.1)',
                    background: display.secondaryColour,
                    flexShrink: 0,
                  }}
                />
                <input
                  type="text"
                  value={display.secondaryColour}
                  disabled={!canManage}
                  onChange={e => this.setDisplay('secondaryColour', e.target.value)}
                  style={{ ...figmaInputStyle, flex: '1 0 0' }}
                />
              </div>
            </FigmaField>
          </FieldRow>

          <FigmaField label="Description">
            <textarea
              value={display.description}
              placeholder="Enter loyalty description"
              disabled={!canManage}
              onChange={e => this.setDisplay('description', e.target.value)}
              style={{ ...figmaTextareaStyle, width: '100%' }}
            />
          </FigmaField>

          <FigmaField label="Logo">
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                background: FG.white,
                border: `1px solid ${FG.inputBorder}`,
                borderRadius: 8,
                padding: '16px 20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  border: `1px solid ${FG.inputBorder}`,
                  borderRadius: 8,
                  boxShadow: '0px 1px 2px rgba(10,13,18,0.05)',
                  flexShrink: 0,
                }}
              >
                <FaCloudUploadAlt size={20} color={FG.sectionDesc} />
              </div>
              <div>
                <div style={{ fontSize: 13, lineHeight: '20px' }}>
                  <span style={{ color: LC.red, fontWeight: 600 }}>Click to upload</span>
                  <span style={{ color: FG.sectionDesc }}> or drag and drop</span>
                </div>
                <div style={{ fontSize: 12, lineHeight: '18px', color: FG.sectionDesc, marginTop: 2 }}>
                  SVG, PNG, JPG or GIF (max. 800×400px)
                </div>
              </div>
            </div>
          </FigmaField>
        </SectionGroup>
      </div>
    );
  }

  // ─── Tab: Platform Connections ─────────────────────────────────────────────

  renderRestaurantRow(restaurant: any, options: { selected?: boolean; onClick?: () => void; onRemove?: () => void }) {
    const name = restaurant?.name || 'Restaurant';
    return (
      <div
        onClick={options.onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: options.selected ? LC.redSoft : FG.white,
          border: `1px solid ${options.selected ? LC.red : FG.inputBorder}`,
          borderRadius: 8,
          padding: 16,
          cursor: options.onClick ? 'pointer' : 'default',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: 99,
            background: '#f5f5f5',
            color: FG.labelColor,
            fontSize: 20,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: '1 0 0', minWidth: 0 }}>
          <div style={{ fontSize: 15, lineHeight: '24px', fontWeight: 600, color: LC.title }}>{name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <span style={{ width: 10, height: 10, borderRadius: 99, background: LC.success }} />
            <span style={{ fontSize: 12, lineHeight: '20px', fontWeight: 500, color: LC.success }}>Active</span>
          </div>
        </div>
        {options.selected && options.onClick && <FaCheckCircle size={20} color={LC.red} />}
        {options.onRemove && (
          <FaTimes
            size={18}
            color={FG.labelColor}
            style={{ cursor: 'pointer' }}
            onClick={(e: any) => {
              e.stopPropagation();
              options.onRemove!();
            }}
          />
        )}
      </div>
    );
  }

  renderPlatformTab(canManage: boolean) {
    const { linkedRestaurantIds } = this.state;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <SectionGroup
          title="Loyalty Organization Settings"
          description="Manage organizations and their connected stores. Choose which stores use shared loyalty settings, or customize rules for individual stores."
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: LC.title }}>Restaurant List</span>
            <button
              type="button"
              disabled={!canManage}
              onClick={() => this.setState({ addRestaurantActive: true, restaurantSelection: [], restaurantQuery: '' })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 12px',
                background: FG.navy,
                border: 'none',
                borderRadius: 8,
                color: FG.white,
                fontSize: 12,
                fontWeight: 600,
                cursor: canManage ? 'pointer' : 'not-allowed',
                opacity: canManage ? 1 : 0.6,
              }}
            >
              <FaPlus size={12} />
              Add New
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {linkedRestaurantIds.length === 0 && (
              <p style={{ margin: 0, fontSize: 13, color: FG.sectionDesc }}>No restaurants added to this loyalty organisation yet.</p>
            )}
            {linkedRestaurantIds.map(id => (
              <React.Fragment key={id}>
                {this.renderRestaurantRow(this.restaurantById(id) || { name: id }, {
                  onRemove: () =>
                    this.setState({ linkedRestaurantIds: linkedRestaurantIds.filter(item => item !== id) }),
                })}
              </React.Fragment>
            ))}
          </div>
        </SectionGroup>
      </div>
    );
  }

  renderAddRestaurantModal() {
    const { addRestaurantActive, restaurantQuery, restaurantSelection, linkedRestaurantIds } = this.state;
    const query = restaurantQuery.trim().toLowerCase();
    const options = this.restaurants().filter((r: any) => {
      if (linkedRestaurantIds.indexOf(r._id) !== -1) return false;
      return !query || String(r.name || '').toLowerCase().indexOf(query) !== -1;
    });

    return (
      <Modal
        id="loyalty-add-restaurant-modal"
        width={520}
        active={addRestaurantActive}
        alignTop={true}
        close={() => this.setState({ addRestaurantActive: false })}
      >
        <ModalContent paddingtb={24} paddinglr={24}>
          <div style={{ fontSize: 20, lineHeight: '32px', fontWeight: 700, color: LC.title, marginBottom: 16 }}>
            Add Restaurant
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: FG.white,
              border: `1px solid ${FG.inputBorder}`,
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 20,
            }}
          >
            <FaSearch size={16} color={FG.sectionDesc} />
            <input
              type="text"
              value={restaurantQuery}
              placeholder="Search Restaurant"
              onChange={e => this.setState({ restaurantQuery: e.target.value })}
              style={{ flex: '1 0 0', border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: FG.labelColor }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {options.length === 0 && <p style={{ margin: 0, fontSize: 13, color: FG.sectionDesc }}>No restaurants available.</p>}
            {options.map((restaurant: any) => {
              const selected = restaurantSelection.indexOf(restaurant._id) !== -1;
              return (
                <React.Fragment key={restaurant._id}>
                  {this.renderRestaurantRow(restaurant, {
                    selected,
                    onClick: () =>
                      this.setState({
                        restaurantSelection: selected
                          ? restaurantSelection.filter(id => id !== restaurant._id)
                          : restaurantSelection.concat([restaurant._id]),
                      }),
                  })}
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <LoyaltyButton style={{ flex: '1 0 0' }} onClick={() => this.setState({ addRestaurantActive: false })}>
              Cancel
            </LoyaltyButton>
            <LoyaltyButton
              variant="primary"
              style={{ flex: '1 0 0' }}
              disabled={restaurantSelection.length === 0}
              onClick={() =>
                this.setState({
                  linkedRestaurantIds: linkedRestaurantIds.concat(restaurantSelection),
                  restaurantSelection: [],
                  addRestaurantActive: false,
                })
              }
            >
              Add
            </LoyaltyButton>
          </div>
        </ModalContent>
      </Modal>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  render() {
    const { loading, error, saving, settingsTab } = this.state;
    const canManage = this.canManage();

    if (loading) {
      return (
        <LoyaltyShell>
          <div className="m-t-12">
            <RotateLoader />
          </div>
        </LoyaltyShell>
      );
    }

    const tabs: { label: string; value: SettingsTab }[] = [
      { label: 'Loyalty Points', value: 'points' },
      { label: 'Gift Cards', value: 'gift_cards' },
      { label: 'Display', value: 'display' },
      { label: 'Platform Connections', value: 'platform' },
    ];

    return (
      <LoyaltyShell>
        {error && <FormGroup error={error} />}
        {!canManage && !this.hasManagePermission() && (
          <FormGroup help="You can view these settings. Managing the programme requires the Manage Programme permission." />
        )}
        {!canManage && this.hasManagePermission() && (
          <FormGroup help="This restaurant shares its loyalty programme with the rest of the organisation — settings are view-only here. Split this restaurant into its own programme to manage it independently." />
        )}

        <Formik enableReinitialize initialValues={this.initialValues()} onSubmit={this.save}>
          {form => (
            <Form>
              <LoyaltyCard style={{ padding: 0 }}>
                {/* Card header */}
                <div style={{ padding: '20px 24px 0' }}>
                  <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0c202a' }}>Settings</h2>

                  {/* Tab navigation */}
                  <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${FG.inputBorder}` }}>
                    {tabs.map(tab => {
                      const active = settingsTab === tab.value;
                      return (
                        <button
                          key={tab.value}
                          type="button"
                          onClick={() => this.setState({ settingsTab: tab.value })}
                          style={{
                            padding: '8px 12px',
                            marginBottom: -1,
                            border: 'none',
                            borderBottom: active ? `2px solid ${FG.navy}` : '2px solid transparent',
                            background: 'transparent',
                            color: active ? FG.navy : FG.sectionDesc,
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: '24px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Tab content */}
                <div style={{ padding: 24 }}>
                  {settingsTab === 'points' && this.renderPointsTab(form, canManage)}
                  {settingsTab === 'gift_cards' && this.renderGiftCardTab(form, canManage)}
                  {settingsTab === 'display' && this.renderDisplayTab(canManage)}
                  {settingsTab === 'platform' && this.renderPlatformTab(canManage)}
                </div>

                {/* Footer */}
                {canManage && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: '16px 24px',
                      borderTop: `1px solid ${FG.inputBorder}`,
                    }}
                  >
                    <LoyaltyButton variant="primary" type="submit" disabled={saving}>
                      {saving && <RotateLoader size={2} color="white" />}
                      {!saving && 'Save Changes'}
                    </LoyaltyButton>
                  </div>
                )}
              </LoyaltyCard>
            </Form>
          )}
        </Formik>

        {this.renderAddRestaurantModal()}
      </LoyaltyShell>
    );
  }
}
